// Global Instructions Rule Applied!
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../state_manager/hooks/useAppDispatch.ts'
import type { RootState } from '../../state_manager/store.ts'
import { Wrapper } from './wrapper.tsx'
import { useIsMobile } from '../hooks/useIsMobile.ts'
import MobileShell from './mobile/MobileShell.tsx'
import {
  filterMenuDataForDisplay,
  getMenuConfigForArea,
  getSelectedMenuKeys,
  resolveDisplayAreaKey,
  resolveUnambiguousAreaKey
} from '../../ui_configs/left_menu.ts'
import { selectMenuAreaKey, setAreaFromRoute } from '../../store/slices/menuAreaSlice.ts'
import { readStoredAreaKey, PREF_APP_CURRENT_AREA } from '../../app_modules/global_configs/appPreferences.ts'
import { persistAppPreference } from '../../app_modules/global_configs/controller.ts'
import { setUserPreferencesAppData } from '../../state_manager/state_update_actions/global_configs/index.ts'
import { useDrawer } from '../contexts/DrawerContext.jsx'
import { RightMenuProvider, useRightMenu } from '../contexts/RightMenuContext.jsx'
import { selectActiveTab, updateTabTitle } from '../../store/slices/tabSlice'
import { createDealService } from '../../state_manager/state_update_actions/deals/deal_services.ts'
import { assignNewDealSequence } from '../utils/newDealSequence.ts'
import { selectDealById } from '../../state_manager/state_update_actions/deals/selectors.ts'
import { selectCompanyById } from '../../state_manager/state_update_actions/companies/selectors.ts'
import { selectProspectById } from '../../state_manager/state_update_actions/fundraising/selectors.ts'
import { selectFundById } from '../../state_manager/state_update_actions/funds/selectors.ts'
import { FundRecordFields } from '../../state_manager/data_models/fund_record.ts'
import { selectRoundById } from '../../state_manager/state_update_actions/rounds/selectors.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  removeActiveRecord
} from '../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../state_manager/state_update_actions/active_records/selectors.ts'
import {
  createCompanyService,
  updateCompanyService
} from '../../state_manager/state_update_actions/companies/company_services.ts'
import { updateCompanyRecord, removeCompanyFromList } from '../../state_manager/state_update_actions/companies/index.ts'
import { updateContactRecord, removeContactFromList } from '../../state_manager/state_update_actions/contacts/index.ts'
import { updateDealRecord, removeDealFromList } from '../../state_manager/state_update_actions/deals/deal_actions.ts'
import {
  updateProspectRecord,
  removeProspectFromList
} from '../../state_manager/state_update_actions/fundraising/index.ts'
import {
  createContactService,
  updateContactService
} from '../../state_manager/state_update_actions/contacts/contact_services.ts'
import {
  createFundraisingService,
  updateFundraisingService
} from '../../state_manager/state_update_actions/fundraising/fundraising_services.ts'
import { AccountRecordFields, type AccountRecord } from '../../state_manager/data_models/account_record.ts'
import { DealFields, type DealRecord } from '../../state_manager/data_models/deal_record.ts'
import type { AgentProvenanceEntry } from '../../state_manager/data_models/agent_provenance_entry.ts'
import { ContactRecordFields, type ContactRecord } from '../../state_manager/data_models/contact_record.ts'
import type { ProspectRecord } from '../../state_manager/interfaces/fundraising.ts'
import { RoundEntryFields } from '../../state_manager/data_models/round_entry.ts'
import { createProspectThunk } from '../../store/slices/prospectsSlice'
import { Drawer as DealDrawerExisting } from '../../app_modules/deals/components/forms/DrawerExisting.tsx'
import { Drawer as DealDrawerNew } from '../../app_modules/deals/components/forms/DrawerNew.tsx'
import { Drawer as CompanyDrawerExisting } from '../../app_modules/companies/components/forms/DrawerExisting.tsx'
import { Drawer as CompanyDrawerNew } from '../../app_modules/companies/components/forms/DrawerNew.tsx'
import { Drawer as ContactDrawerExisting } from '../../app_modules/contacts/components/forms/DrawerExisting.tsx'
import { Drawer as ContactDrawerNew } from '../../app_modules/contacts/components/forms/DrawerNew.tsx'
import { Drawer as FundraisingDrawerExisting } from '../../app_modules/fundraising/components/forms/DrawerExisting.tsx'
import { Drawer as FundraisingDrawerNew } from '../../app_modules/fundraising/components/forms/DrawerNew.tsx'
import { toast } from 'react-toastify'
import { dismissErrorToast, showErrorToast } from '../utils/errorToast.tsx'
import { fetchDealById, deleteDeal } from '../../app_modules/deals/services/dealsService.ts'
import { fetchCompanyById, deleteCompany } from '../../app_modules/companies/services/companiesService.ts'
import { fetchContactById, deleteContact } from '../../app_modules/contacts/services/contactsService.ts'
import { fetchFundraisingById, deleteProspect } from '../../app_modules/fundraising/services/fundraisingService.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'
import {
  CONFIRM_DELETE_CONTACT_TITLE,
  CONFIRM_DELETE_COMPANY_TITLE,
  CONFIRM_DELETE_DEAL_TITLE,
  CONFIRM_DELETE_PROSPECT_TITLE
} from '../../state_manager/constants/strings.ts'

/**
 * The record a drawer shows: a deal / company / contact / prospect from its
 * module slice or a detail fetch, an activeRecords entry, or the empty
 * temporary record a "new" drawer starts from.
 */
type DrawerRecord = DealRecord | AccountRecord | ContactRecord | ProspectRecord | Record<string, unknown>

/** A string-valued field of the drawer record, or null when absent / not a string. */
const recordText = (record: DrawerRecord | null | undefined, key: string): string | null => {
  const value = record ? Object.entries(record).find(([k]) => k === key)?.[1] : null
  return typeof value === 'string' && value ? value : null
}

/** The detail-fetch result shapes handleDrawerRefresh can receive. */
type DrawerFetchResult =
  | Awaited<ReturnType<typeof fetchDealById>>
  | Awaited<ReturnType<typeof fetchCompanyById>>
  | Awaited<ReturnType<typeof fetchContactById>>
  | Awaited<ReturnType<typeof fetchFundraisingById>>

/**
 * Inner component that uses the RightMenu context
 */
function MainTabContainerInner() {
  // Track what changed to cause this render
  const prevValuesRef = useRef<Record<string, unknown>>({})
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()

  const activeTab = useSelector(selectActiveTab)
  const globalConfigs = useSelector((state: RootState) => state.globalConfigs)

  // Drawer functionality - call early so we can use these values in change tracking
  const { isOpen: isDrawerOpen, recordId, isNew, closeDrawer, formType } = useDrawer()

  // Update ref for next render
  prevValuesRef.current = {
    activeTabId: activeTab?.id,
    activeTabModule: activeTab?.module,
    pathname: location.pathname,
    recordId,
    isDrawerOpen,
    isNew
  }

  // ---------------------------------------------------------------------------
  // Left-nav area. ONE resolved value feeds both the menu body and the footer
  // switcher, so they cannot disagree. Precedence: explicit state (a footer
  // pick or an unambiguous navigation) -> stored per-user preference -> the
  // default area.
  const areaKey = useSelector(selectMenuAreaKey)
  const storedAreaKey = useMemo(() => readStoredAreaKey(globalConfigs), [globalConfigs])

  const displayAreaKey = useMemo(
    () => resolveDisplayAreaKey(areaKey ?? storedAreaKey, globalConfigs),
    [areaKey, storedAreaKey, globalConfigs]
  )

  // Non-admins see the Configuration area without its admin-only items
  // (display filter only — the /config/* routes enforce with 403s). Area
  // resolution below stays on the UNFILTERED payload so deep links to hidden
  // items still resolve their area.
  // Fail closed on both: anything other than an explicit true hides the row.
  const isAppAdmin = globalConfigs?.is_app_admin === true
  const hasConnectedAgent = globalConfigs?.has_connected_agent === true
  const menuDataForDisplay = useMemo(
    () => filterMenuDataForDisplay(globalConfigs, { isAppAdmin, hasConnectedAgent }),
    [globalConfigs, isAppAdmin, hasConnectedAgent]
  )

  const menuConfig = useMemo(
    () => getMenuConfigForArea(menuDataForDisplay, displayAreaKey),
    [menuDataForDisplay, displayAreaKey]
  )

  const selectedMenuKeys = useMemo(
    () => getSelectedMenuKeys(displayAreaKey, location.pathname, menuDataForDisplay),
    [displayAreaKey, location.pathname, menuDataForDisplay]
  )

  // ONE-SHOT area seed (owner 2026-09-02). The route no longer steers the
  // sidebar: focusing a tab, opening a record form or following a global-search
  // hit all leave the area exactly where the user put it. The single exception
  // is a cold load by someone who has never chosen an area — a deep link would
  // otherwise open the default pane rather than the one that owns the route.
  //
  // Precedence is therefore: explicit pick > stored preference > this seed.
  // The ref makes it fire at most once per session; the `!next` return does NOT
  // burn the shot, because globalConfigs arrives asynchronously and the first
  // run legitimately has no cfg to resolve against.
  const areaSeededRef = useRef(false)
  useEffect(() => {
    if (areaSeededRef.current) return
    if (areaKey || storedAreaKey) {
      areaSeededRef.current = true
      return
    }
    const next = resolveUnambiguousAreaKey(location.pathname, globalConfigs)
    if (!next) return
    areaSeededRef.current = true
    dispatch(setAreaFromRoute(next))
  }, [location.pathname, globalConfigs, areaKey, storedAreaKey, dispatch])

  // Persist the area so a hard refresh reopens where the user left off. Gated
  // on a CHANGE, not on navigation: moving between pages inside an area leaves
  // displayAreaKey untouched, so ordinary navigation writes nothing. The ref is
  // seeded from what the server already had, so a cold load never rewrites it.
  const lastPersistedAreaRef = useRef<string | null>(null)
  const appInitialized = useSelector((state: RootState) => state.auth?.appInitialized)

  useEffect(() => {
    if (!appInitialized || !displayAreaKey) return
    if (lastPersistedAreaRef.current === null) lastPersistedAreaRef.current = storedAreaKey
    if (displayAreaKey === lastPersistedAreaRef.current) return

    lastPersistedAreaRef.current = displayAreaKey
    dispatch(setUserPreferencesAppData({ [PREF_APP_CURRENT_AREA]: displayAreaKey }))
    // Best-effort: the area is client state and persistence is a convenience,
    // so a failed write is logged by the controller and never rolled back —
    // rolling back would yank the user out of the area they just picked.
    void persistAppPreference({ [PREF_APP_CURRENT_AREA]: displayAreaKey })
  }, [appInitialized, displayAreaKey, storedAreaKey, dispatch])

  const deals = useSelector((state: RootState) => state.deals?.deal?.data || [])

  const pipelineDeals = useSelector((state: RootState) => state.deals?.pipeline?.data || [])

  const { width: rightMenuWidth } = useRightMenu()

  const [selectedRecord, setSelectedRecord] = useState<DrawerRecord | null>(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const drawerFormRef = useRef<{ submit: () => Promise<void> } | null>(null)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null) // Ref to store InteractionsSection refresh function
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null) // Ref to store TasksSection refresh function
  const refreshInvestmentMeasuresRef = useRef<(() => Promise<void>) | null>(null) // Ref to store InvestmentSummarySection refresh function
  // Track drawer open count per module for new drawers to force remount on reopen
  const [newDrawerOpenCount, setNewDrawerOpenCount] = useState<Record<string, number>>({})

  // Track when drawer was last closed per module to detect fresh opens
  const drawerCloseTimeRef = useRef<Record<string, number>>({})

  // Track previous selectedRecord to prevent unnecessary effect runs
  const previousSelectedRecordRef = useRef<DrawerRecord | null>(null)

  // Track previous values for change detection
  if (!prevValuesRef.current) {
    prevValuesRef.current = {}
  }

  // Get records from activeRecords first, then fallback to module state
  // Memoize selector functions to prevent recreation on every render
  const isDealsModule = useMemo(
    () => activeTab?.module === 'deals' || activeTab?.module === 'deal-pipeline',
    [activeTab?.module]
  )
  const isCompaniesModule = useMemo(() => activeTab?.module === 'companies', [activeTab?.module])
  const isContactsModule = useMemo(() => activeTab?.module === 'contacts', [activeTab?.module])
  const isFundraisingModule = useMemo(
    () => activeTab?.module === 'fundraising' || activeTab?.module === 'fundraising-pipeline',
    [activeTab?.module]
  )
  const isFundsModule = useMemo(() => activeTab?.module === 'funds', [activeTab?.module])
  const shouldFetchRecord = useMemo(() => recordId && !isNew, [recordId, isNew])

  // Only run selectors for active module - use stable selector functions
  const dealFromActiveRecordsSelector = useMemo(
    () => (state: RootState) => (isDealsModule && shouldFetchRecord ? selectActiveRecordById(state, recordId) : null),
    [isDealsModule, shouldFetchRecord, recordId]
  )
  const dealFromActiveRecords = useSelector(dealFromActiveRecordsSelector)

  const dealFromStateSelector = useMemo(
    () => (state: RootState) => (isDealsModule && shouldFetchRecord ? selectDealById(state, recordId) : null),
    [isDealsModule, shouldFetchRecord, recordId]
  )
  const dealFromState = useSelector(dealFromStateSelector)

  const companyFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isCompaniesModule && shouldFetchRecord ? selectActiveRecordById(state, recordId) : null,
    [isCompaniesModule, shouldFetchRecord, recordId]
  )
  const companyFromActiveRecords = useSelector(companyFromActiveRecordsSelector)

  const companyFromStateSelector = useMemo(
    () => (state: RootState) => (isCompaniesModule && shouldFetchRecord ? selectCompanyById(state, recordId) : null),
    [isCompaniesModule, shouldFetchRecord, recordId]
  )
  const companyFromState = useSelector(companyFromStateSelector)

  const contactFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isContactsModule && shouldFetchRecord ? selectActiveRecordById(state, recordId) : null,
    [isContactsModule, shouldFetchRecord, recordId]
  )
  const contactFromActiveRecords = useSelector(contactFromActiveRecordsSelector)

  const contactFromStateSelector = useMemo(
    () => (state: RootState) => {
      if (isContactsModule && shouldFetchRecord) {
        const contacts = state.contacts?.data || []
        return contacts.find((c) => c[ContactRecordFields.contacts__id]?.toString() === String(recordId)) || null
      }
      return null
    },
    [isContactsModule, shouldFetchRecord, recordId]
  )
  const contactFromState = useSelector(contactFromStateSelector)

  const prospectFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isFundraisingModule && shouldFetchRecord ? selectActiveRecordById(state, recordId) : null,
    [isFundraisingModule, shouldFetchRecord, recordId]
  )
  const prospectFromActiveRecords = useSelector(prospectFromActiveRecordsSelector)

  const prospectFromStateSelector = useMemo(
    () => (state: RootState) =>
      isFundraisingModule && shouldFetchRecord
        ? selectProspectById({ fundraising: state.fundraising }, recordId)
        : null,
    [isFundraisingModule, shouldFetchRecord, recordId]
  )
  const prospectFromState = useSelector(prospectFromStateSelector)

  // Get records from activeRecords first, then fallback to state for tab titles
  // Only calculate for active tab's module to optimize performance
  const activeTabModuleForTitle = activeTab?.module
  const activeTabRecordId = activeTab?.recordId
  const activeTabIsNew = activeTab?.isNew
  const shouldCalculateTabTitle = useMemo(
    () => activeTabRecordId && !activeTabIsNew,
    [activeTabRecordId, activeTabIsNew]
  )

  // Memoize selector functions for tab titles - only calculate for active module
  const dealForTabTitleFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isDealsModule && shouldCalculateTabTitle ? selectActiveRecordById(state, activeTabRecordId) : null,
    [isDealsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const dealForTabTitleFromActiveRecords = useSelector(dealForTabTitleFromActiveRecordsSelector)

  const dealForTabTitleSelector = useMemo(
    () => (state: RootState) =>
      isDealsModule && shouldCalculateTabTitle ? selectDealById(state, activeTabRecordId) : null,
    [isDealsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const dealForTabTitle = useSelector(dealForTabTitleSelector)
  const dealForTabTitleFinal = dealForTabTitleFromActiveRecords || dealForTabTitle

  const companyForTabTitleFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isCompaniesModule && shouldCalculateTabTitle ? selectActiveRecordById(state, activeTabRecordId) : null,
    [isCompaniesModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const companyForTabTitleFromActiveRecords = useSelector(companyForTabTitleFromActiveRecordsSelector)

  const companyForTabTitleSelector = useMemo(
    () => (state: RootState) =>
      isCompaniesModule && shouldCalculateTabTitle ? selectCompanyById(state, activeTabRecordId) : null,
    [isCompaniesModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const companyForTabTitle = useSelector(companyForTabTitleSelector)
  const companyForTabTitleFinal = companyForTabTitleFromActiveRecords || companyForTabTitle

  const contactForTabTitleFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isContactsModule && shouldCalculateTabTitle ? selectActiveRecordById(state, activeTabRecordId) : null,
    [isContactsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const contactForTabTitleFromActiveRecords = useSelector(contactForTabTitleFromActiveRecordsSelector)

  const contactForTabTitleSelector = useMemo(
    () => (state: RootState) => {
      if (isContactsModule && shouldCalculateTabTitle) {
        const contacts = state.contacts?.data || []
        return (
          contacts.find((c) => c[ContactRecordFields.contacts__id]?.toString() === String(activeTabRecordId)) || null
        )
      }
      return null
    },
    [isContactsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const contactForTabTitle = useSelector(contactForTabTitleSelector)
  const contactForTabTitleFinal = contactForTabTitleFromActiveRecords || contactForTabTitle

  const prospectForTabTitleFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isFundraisingModule && shouldCalculateTabTitle ? selectActiveRecordById(state, activeTabRecordId) : null,
    [isFundraisingModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const prospectForTabTitleFromActiveRecords = useSelector(prospectForTabTitleFromActiveRecordsSelector)

  const prospectForTabTitleSelector = useMemo(
    () => (state: RootState) =>
      isFundraisingModule && shouldCalculateTabTitle
        ? selectProspectById({ fundraising: state.fundraising }, activeTabRecordId)
        : null,
    [isFundraisingModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const prospectForTabTitle = useSelector(prospectForTabTitleSelector)
  const prospectForTabTitleFinal = prospectForTabTitleFromActiveRecords || prospectForTabTitle

  const fundForTabTitleFromActiveRecordsSelector = useMemo(
    () => (state: RootState) =>
      isFundsModule && shouldCalculateTabTitle ? selectActiveRecordById(state, activeTabRecordId) : null,
    [isFundsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const fundForTabTitleFromActiveRecords = useSelector(fundForTabTitleFromActiveRecordsSelector)

  const fundForTabTitleSelector = useMemo(
    () => (state: RootState) =>
      isFundsModule && shouldCalculateTabTitle ? selectFundById(state, activeTabRecordId) : null,
    [isFundsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const fundForTabTitle = useSelector(fundForTabTitleSelector)
  const fundForTabTitleFinal = fundForTabTitleFromActiveRecords || fundForTabTitle

  // Get round from state using selector for tab title (for full forms)
  const isRoundsModule = useMemo(() => activeTabModuleForTitle === 'rounds', [activeTabModuleForTitle])
  const roundForTabTitleSelector = useMemo(
    () => (state: RootState) =>
      isRoundsModule && shouldCalculateTabTitle ? selectRoundById(state, activeTabRecordId) : null,
    [isRoundsModule, shouldCalculateTabTitle, activeTabRecordId]
  )
  const roundForTabTitle = useSelector(roundForTabTitleSelector)

  // Track previous recordId to prevent unnecessary updates when switching tabs
  const previousRecordIdRef = useRef<string | null>(null)
  const previousModuleRef = useRef<string | null>(null)
  const previousIsNewRef = useRef<boolean | null>(null)

  // Track which recordId+module combinations we've already processed to prevent duplicate effect runs
  const processedRecordsRef = useRef(new Set<string>())

  // Track previous record object references to detect when Redux updates create new object references
  const previousDealFromActiveRecordsRef = useRef<DrawerRecord | null | undefined>(null)
  const previousDealFromStateRef = useRef<DrawerRecord | null | undefined>(null)
  const previousCompanyFromActiveRecordsRef = useRef<DrawerRecord | null | undefined>(null)
  const previousCompanyFromStateRef = useRef<DrawerRecord | null | undefined>(null)
  const previousContactFromActiveRecordsRef = useRef<DrawerRecord | null | undefined>(null)
  const previousContactFromStateRef = useRef<DrawerRecord | null | undefined>(null)
  const previousProspectFromActiveRecordsRef = useRef<DrawerRecord | null | undefined>(null)
  const previousProspectFromStateRef = useRef<DrawerRecord | null | undefined>(null)

  // Get temporary record from activeRecords for new drawers
  const tempRecordUuid = useMemo(() => {
    if (isNew && activeTab?.module) {
      // Use module name directly - deals and deal-pipeline have separate temporary records
      return `${activeTab.module}-new`
    }
    return null
  }, [isNew, activeTab?.module])

  const tempRecordFromActiveRecords = useSelector((state: RootState) =>
    tempRecordUuid ? selectActiveRecordById(state, tempRecordUuid) : null
  )

  // Handle temporary records for new drawers
  useEffect(() => {
    if (isNew && activeTab?.module && tempRecordUuid && isDrawerOpen) {
      const moduleName = activeTab.module
      const wasRecentlyClosed =
        drawerCloseTimeRef.current[moduleName] && Date.now() - drawerCloseTimeRef.current[moduleName] < 100 // 100ms threshold

      // If drawer was recently closed, always start fresh (cleanup should have happened)
      if (wasRecentlyClosed) {
        // Clear any existing temporary record and create fresh one
        dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
        dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
        dispatch(setActiveRecord({ uuid: tempRecordUuid, record: {} }))
        dispatch(registerRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
        const emptyRecord = {}
        setSelectedRecord(emptyRecord)
        previousSelectedRecordRef.current = emptyRecord
        // Clear the close time marker
        delete drawerCloseTimeRef.current[moduleName]
      } else if (tempRecordFromActiveRecords) {
        // Use existing temporary record (user navigated between tabs - preserve form state)
        if (previousSelectedRecordRef.current !== tempRecordFromActiveRecords) {
          setSelectedRecord(tempRecordFromActiveRecords)
          previousSelectedRecordRef.current = tempRecordFromActiveRecords
        }
      } else {
        // Create fresh empty temporary record (first open or after cleanup)
        dispatch(setActiveRecord({ uuid: tempRecordUuid, record: {} }))
        dispatch(registerRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
        const emptyRecord = {}
        setSelectedRecord(emptyRecord)
        previousSelectedRecordRef.current = emptyRecord
      }
    }
  }, [isNew, activeTab?.module, tempRecordUuid, isDrawerOpen, tempRecordFromActiveRecords, dispatch])

  // Combined effect to update selectedRecord from multiple sources (optimized to prevent duplicate updates)
  useEffect(() => {
    const recordIdString = recordId?.toString()
    const previousRecordIdString = previousRecordIdRef.current?.toString()
    const moduleChanged = activeTab?.module !== previousModuleRef.current
    const transitioningFromNewToExisting = previousIsNewRef.current === true && isNew === false

    // Create a unique key for this recordId+module combination
    const recordKey = recordIdString && activeTab?.module ? `${activeTab.module}-${recordIdString}` : null

    // Check if record object references changed (even if recordId stayed the same)
    const dealObjectChanged =
      dealFromActiveRecords !== previousDealFromActiveRecordsRef.current ||
      dealFromState !== previousDealFromStateRef.current
    const companyObjectChanged =
      companyFromActiveRecords !== previousCompanyFromActiveRecordsRef.current ||
      companyFromState !== previousCompanyFromStateRef.current
    const contactObjectChanged =
      contactFromActiveRecords !== previousContactFromActiveRecordsRef.current ||
      contactFromState !== previousContactFromStateRef.current
    const prospectObjectChanged =
      prospectFromActiveRecords !== previousProspectFromActiveRecordsRef.current ||
      prospectFromState !== previousProspectFromStateRef.current
    const recordObjectChanged =
      dealObjectChanged || companyObjectChanged || contactObjectChanged || prospectObjectChanged

    // If transitioning from new to existing, reset previousSelectedRecordRef to force reload
    if (transitioningFromNewToExisting) {
      previousSelectedRecordRef.current = null
      processedRecordsRef.current.clear()
    }

    // Skip if we've already processed this exact combination and nothing meaningful changed
    // BUT allow it to proceed if the record object reference changed (Redux update) or transitioning from new to existing
    if (
      recordKey &&
      processedRecordsRef.current.has(recordKey) &&
      recordIdString === previousRecordIdString &&
      !moduleChanged &&
      !transitioningFromNewToExisting &&
      previousSelectedRecordRef.current &&
      !recordObjectChanged
    ) {
      return
    }

    // Priority 1: Temporary records for new drawers
    if (
      isNew &&
      tempRecordUuid &&
      tempRecordFromActiveRecords &&
      previousSelectedRecordRef.current !== tempRecordFromActiveRecords
    ) {
      setSelectedRecord(tempRecordFromActiveRecords)
      previousSelectedRecordRef.current = tempRecordFromActiveRecords
      previousIsNewRef.current = isNew
      return
    }

    // Skip rest of logic for new drawers
    if (isNew) {
      previousIsNewRef.current = isNew
      return
    }

    // Only update if recordId actually changed or module changed (different drawer)
    if (!recordId || !activeTab?.module) {
      if (!recordId && previousSelectedRecordRef.current !== null) {
        setSelectedRecord(null)
        previousSelectedRecordRef.current = null
        previousRecordIdRef.current = null
        previousModuleRef.current = null
        previousIsNewRef.current = null
        processedRecordsRef.current.clear() // Clear processed records when drawer closes
      }
      return
    }

    // Clear processed records if module changed (switching between different module types)
    if (moduleChanged && previousModuleRef.current) {
      processedRecordsRef.current.clear()
    }

    // Check if we need to update (recordId changed or module changed or record object changed)
    // Use ref to compare selectedRecord to avoid dependency on state
    // Allow update if record object reference changed (Redux update) even if recordId stayed the same
    // Also allow update if transitioning from new to existing
    if (
      recordIdString === previousRecordIdString &&
      !moduleChanged &&
      !transitioningFromNewToExisting &&
      previousSelectedRecordRef.current &&
      !recordObjectChanged
    ) {
      return
    }

    // Check route to ensure we're on the correct route
    const isDealsRoute = formType === 'deal' || location.pathname.startsWith('/deals')
    const isCompaniesRoute = formType === 'company' || location.pathname.startsWith('/companies')
    const isContactsRoute = formType === 'contact' || location.pathname.startsWith('/contacts')
    const isFundraisingRoute =
      formType === 'prospect' ||
      location.pathname.startsWith('/fundraising') ||
      location.pathname.startsWith('/prospects') ||
      location.pathname.startsWith('/fundraising/pipeline')

    // Priority 2: Deal from active records or state (for existing deal drawers)
    if ((activeTab.module === 'deals' || activeTab.module === 'deal-pipeline') && isDealsRoute) {
      const deal = dealFromActiveRecords || dealFromState
      if (deal && previousSelectedRecordRef.current !== deal) {
        setSelectedRecord(deal)
        previousSelectedRecordRef.current = deal
      }
      // Update refs to track current deal object references
      previousDealFromActiveRecordsRef.current = dealFromActiveRecords
      previousDealFromStateRef.current = dealFromState
      // Fetch deal if not in activeRecords or state
      if (!deal && recordId) {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) return
        setDrawerLoading(true)
        fetchDealById(recordId, currentToken)
          .then((result) => {
            if (result.data) {
              setSelectedRecord(result.data)
              previousSelectedRecordRef.current = result.data
              const dealIdString = recordId.toString()
              dispatch(setActiveRecord({ uuid: dealIdString, record: result.data }))
              dispatch(registerRecordUsage({ uuid: dealIdString, context: 'drawer' }))
            }
          })
          .catch((error) => {
            console.error('Error fetching deal:', error)
          })
          .finally(() => {
            setDrawerLoading(false)
          })
      }
      previousRecordIdRef.current = recordId
      previousModuleRef.current = activeTab.module
      previousIsNewRef.current = isNew
      if (recordKey) processedRecordsRef.current.add(recordKey)
      return
    }

    // Priority 3: Company from active records or state (for existing company drawers)
    if (activeTab.module === 'companies' && isCompaniesRoute) {
      const company = companyFromActiveRecords || companyFromState
      if (company && previousSelectedRecordRef.current !== company) {
        setSelectedRecord(company)
        previousSelectedRecordRef.current = company
      }
      // Update refs to track current company object references
      previousCompanyFromActiveRecordsRef.current = companyFromActiveRecords
      previousCompanyFromStateRef.current = companyFromState
      // Fetch company if not in activeRecords or state
      if (!company && recordId) {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) return
        setDrawerLoading(true)
        fetchCompanyById(recordId, currentToken)
          .then((result) => {
            if (result.data) {
              setSelectedRecord(result.data)
              previousSelectedRecordRef.current = result.data
              const companyIdString = recordId.toString()
              dispatch(setActiveRecord({ uuid: companyIdString, record: result.data }))
              dispatch(registerRecordUsage({ uuid: companyIdString, context: 'drawer' }))
            }
          })
          .catch((error) => {
            console.error('Error fetching company:', error)
          })
          .finally(() => {
            setDrawerLoading(false)
          })
      }
      previousRecordIdRef.current = recordId
      previousModuleRef.current = activeTab.module
      previousIsNewRef.current = isNew
      if (recordKey) processedRecordsRef.current.add(recordKey)
      return
    }

    // Priority 4: Contact from active records or state (for existing contact drawers)
    if (activeTab.module === 'contacts') {
      const contact = contactFromActiveRecords || contactFromState
      if (contact && previousSelectedRecordRef.current !== contact) {
        setSelectedRecord(contact)
        previousSelectedRecordRef.current = contact
      }
      // Update refs to track current contact object references
      previousContactFromActiveRecordsRef.current = contactFromActiveRecords
      previousContactFromStateRef.current = contactFromState
      // Fetch contact if not in activeRecords or state
      if (!contact && recordId) {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) return
        setDrawerLoading(true)
        fetchContactById(recordId, currentToken)
          .then((result) => {
            if (result.data) {
              setSelectedRecord(result.data)
              previousSelectedRecordRef.current = result.data
              const contactIdString = recordId.toString()
              dispatch(setActiveRecord({ uuid: contactIdString, record: result.data }))
              dispatch(registerRecordUsage({ uuid: contactIdString, context: 'drawer' }))
            }
          })
          .catch((error) => {
            console.error('Error fetching contact:', error)
          })
          .finally(() => {
            setDrawerLoading(false)
          })
      }
      previousRecordIdRef.current = recordId
      previousModuleRef.current = activeTab.module
      previousIsNewRef.current = isNew
      if (recordKey) processedRecordsRef.current.add(recordKey)
      return
    }

    // Priority 4: Contact from active records or state (for existing contact drawers)
    if (activeTab.module === 'contacts' && isContactsRoute) {
      const contact = contactFromActiveRecords || contactFromState
      if (contact && previousSelectedRecordRef.current !== contact) {
        setSelectedRecord(contact)
        previousSelectedRecordRef.current = contact
      }
      // Update refs to track current contact object references
      previousContactFromActiveRecordsRef.current = contactFromActiveRecords
      previousContactFromStateRef.current = contactFromState
      // Fetch contact if not in activeRecords or state
      if (!contact && recordId) {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) return
        setDrawerLoading(true)
        fetchContactById(recordId, currentToken)
          .then((result) => {
            if (result.data) {
              setSelectedRecord(result.data)
              previousSelectedRecordRef.current = result.data
              const contactIdString = recordId.toString()
              dispatch(setActiveRecord({ uuid: contactIdString, record: result.data }))
              dispatch(registerRecordUsage({ uuid: contactIdString, context: 'drawer' }))
            }
          })
          .catch((error) => {
            console.error('Error fetching contact:', error)
          })
          .finally(() => {
            setDrawerLoading(false)
          })
      }
      previousRecordIdRef.current = recordId
      previousModuleRef.current = activeTab.module
      previousIsNewRef.current = isNew
      if (recordKey) processedRecordsRef.current.add(recordKey)
      return
    }

    // Priority 5: Prospect from active records or state (for existing fundraising drawers)
    if ((activeTab.module === 'fundraising' || activeTab.module === 'fundraising-pipeline') && isFundraisingRoute) {
      const prospect = prospectFromActiveRecords || prospectFromState
      if (prospect && previousSelectedRecordRef.current !== prospect) {
        setSelectedRecord(prospect)
        previousSelectedRecordRef.current = prospect
      }
      // Update refs to track current prospect object references
      previousProspectFromActiveRecordsRef.current = prospectFromActiveRecords
      previousProspectFromStateRef.current = prospectFromState
      // Fetch prospect if not in activeRecords or state
      if (!prospect && recordId) {
        const currentToken = getAccessToken(store.getState().auth)
        if (!currentToken) return
        setDrawerLoading(true)
        fetchFundraisingById(recordId, currentToken)
          .then((result) => {
            if (result.data) {
              setSelectedRecord(result.data)
              previousSelectedRecordRef.current = result.data
              const prospectIdString = recordId.toString()
              dispatch(setActiveRecord({ uuid: prospectIdString, record: result.data }))
              dispatch(registerRecordUsage({ uuid: prospectIdString, context: 'drawer' }))
            }
          })
          .catch((error) => {
            console.error('Error fetching prospect:', error)
          })
          .finally(() => {
            setDrawerLoading(false)
          })
      }
      previousRecordIdRef.current = recordId
      previousModuleRef.current = activeTab.module
      previousIsNewRef.current = isNew
      if (recordKey) processedRecordsRef.current.add(recordKey)
      return
    }

    // No matching module
    if (previousSelectedRecordRef.current !== null) {
      setSelectedRecord(null)
      previousSelectedRecordRef.current = null
    }
    previousRecordIdRef.current = recordId
    previousModuleRef.current = activeTab.module
    previousIsNewRef.current = isNew
    if (recordKey) processedRecordsRef.current.add(recordKey)
  }, [
    recordId,
    isNew,
    activeTab?.module,
    tempRecordUuid,
    tempRecordFromActiveRecords,
    dealFromActiveRecords,
    dealFromState,
    companyFromActiveRecords,
    companyFromState,
    contactFromActiveRecords,
    contactFromState,
    prospectFromActiveRecords,
    prospectFromState,
    dispatch,
    formType,
    location.pathname
  ])

  // Combined effect to update tab titles (batched to reduce renders)
  useEffect(() => {
    // Early return if not a full form tab
    const activeTabIsFullForm = activeTab?.isFullForm
    if (!activeTabIsFullForm) {
      return
    }

    const activeTabModule = activeTab.module
    const activeTabIsNew = activeTab.isNew
    const activeTabRecordId = activeTab.recordId
    const activeTabKey = activeTab.key
    const activeTabTitle = activeTab.title

    // Placeholder titles (tabSlice's "Edit <Module> (<id>)" seed and the
    // "Edit <Module> (ID: <id>)" fallbacks below) embed the record id; real
    // record names don't. The record lookups miss whenever a form unmounts
    // and evicts its activeRecords entry, so the ID fallback may only ever
    // replace another placeholder — never a real name already on the tab.
    const titleIsPlaceholder =
      !activeTabTitle || (activeTabRecordId && activeTabTitle.includes(String(activeTabRecordId)))

    let newTitle: string | null = null

    // Deal tabs
    if (activeTabModule === 'deals' || activeTabModule === 'deal-pipeline') {
      if (activeTabIsNew) {
        newTitle = 'New Deal'
      } else if (activeTabRecordId) {
        const deal = dealForTabTitleFinal
        if (deal && deal[DealFields.deals__name]) {
          newTitle = `${deal[DealFields.deals__name]}`
        } else if (titleIsPlaceholder) {
          newTitle = `Edit Deal (ID: ${activeTabRecordId})`
        }
      }
    }
    // Company tabs
    else if (activeTabModule === 'companies') {
      if (activeTabIsNew) {
        newTitle = 'New Company'
      } else if (activeTabRecordId) {
        const company = companyForTabTitleFinal
        if (company && company[AccountRecordFields.accounts__name]) {
          newTitle = `${company[AccountRecordFields.accounts__name]}`
        } else if (titleIsPlaceholder) {
          newTitle = `Edit Company (ID: ${activeTabRecordId})`
        }
      }
    }
    // Contact tabs
    else if (activeTabModule === 'contacts') {
      if (activeTabIsNew) {
        newTitle = 'New Contact'
      } else if (activeTabRecordId) {
        const contact = contactForTabTitleFinal
        if (contact && contact[ContactRecordFields.contacts__last_name]) {
          const firstName = contact[ContactRecordFields.contacts__first_name] || ''
          const lastName = contact[ContactRecordFields.contacts__last_name] || ''
          const name = `${firstName} ${lastName}`.trim() || lastName
          newTitle = name
        } else if (titleIsPlaceholder) {
          newTitle = `Edit Contact (ID: ${activeTabRecordId})`
        }
      }
    }
    // Fund tabs
    else if (activeTabModule === 'funds') {
      if (activeTabIsNew) {
        newTitle = 'New Fund'
      } else if (activeTabRecordId) {
        const fund = fundForTabTitleFinal
        if (fund && fund[FundRecordFields.funds__name]) {
          newTitle = `${fund[FundRecordFields.funds__name]}`
        } else if (titleIsPlaceholder) {
          newTitle = `Edit Fund (ID: ${activeTabRecordId})`
        }
      }
    }
    // Consolidated fund tabs — name resolves from globalConfigs.fund_groups
    // (always loaded), no per-record slice needed
    else if (activeTabModule === 'consolidated-funds') {
      if (activeTabRecordId) {
        const group = (store.getState().globalConfigs?.fund_groups ?? []).find((g) => g.id === activeTabRecordId)
        if (group && group.name) {
          newTitle = group.name
        } else if (titleIsPlaceholder) {
          newTitle = `Consolidated Fund (ID: ${activeTabRecordId})`
        }
      }
    }
    // Prospect tabs
    else if (activeTabModule === 'fundraising') {
      if (activeTabIsNew) {
        newTitle = 'New Prospect'
      } else if (activeTabRecordId) {
        const prospect = prospectForTabTitleFinal
        const prospectName =
          recordText(prospect, 'investor_name') ?? recordText(prospect, AccountRecordFields.accounts__name)
        if (prospectName) {
          newTitle = prospectName
        } else if (titleIsPlaceholder) {
          newTitle = `Edit Prospect (ID: ${activeTabRecordId})`
        }
      }
    }

    // Only update if the title has changed and we have a new title
    if (newTitle && activeTabTitle !== newTitle) {
      dispatch(updateTabTitle({ key: activeTabKey, title: newTitle }))
    }
  }, [
    activeTab?.key,
    activeTab?.module,
    activeTab?.isFullForm,
    activeTab?.isNew,
    activeTab?.recordId,
    activeTab?.title,
    dealForTabTitleFinal,
    companyForTabTitleFinal,
    contactForTabTitleFinal,
    prospectForTabTitleFinal,
    fundForTabTitleFinal,
    dispatch
  ])

  // Update tab title for round full forms
  useEffect(() => {
    if (activeTab && activeTab.module === 'rounds' && activeTab.isFullForm) {
      let newTitle: string

      if (activeTab.isNew) {
        newTitle = 'New Round'
      } else if (activeTab.recordId) {
        // Use round from state selector; if found, use round name
        newTitle = roundForTabTitle?.[RoundEntryFields.rounds__name] || `Edit Round (${activeTab.recordId})`
      } else {
        return // No update needed
      }

      // Only update if the title has changed
      if (activeTab.title !== newTitle) {
        dispatch(updateTabTitle({ key: activeTab.key, title: newTitle }))
      }
    }
  }, [activeTab, roundForTabTitle, dispatch])

  // Handle drawer refresh - fetches the current record again
  const handleDrawerRefresh = useCallback(async () => {
    if (!recordId || !activeTab?.module || isNew) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) return

    setDrawerLoading(true)
    const module = activeTab.module

    try {
      let result: DrawerFetchResult | null = null

      if (module === 'deals' || module === 'deal-pipeline') {
        result = await fetchDealById(recordId, currentToken)
      } else if (module === 'companies') {
        result = await fetchCompanyById(recordId, currentToken)
      } else if (module === 'contacts') {
        result = await fetchContactById(recordId, currentToken)
      } else if (module === 'fundraising' || module === 'fundraising-pipeline') {
        result = await fetchFundraisingById(recordId, currentToken)
      }

      if (result && result.data) {
        setSelectedRecord(result.data)
        previousSelectedRecordRef.current = result.data
        const recordIdString = recordId.toString()
        // Update both activeRecords and module Redux state with full record
        dispatch(setActiveRecord({ uuid: recordIdString, record: result.data }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'drawer' }))
        // Also update module-specific Redux state (companies, deals, contacts, fundraising)
        if (module === 'companies') {
          dispatch(updateCompanyRecord({ companyId: recordIdString, updates: result.data }))
        } else if (module === 'contacts') {
          dispatch(updateContactRecord({ contactId: recordIdString, updates: result.data }))
        } else if (module === 'deals' || module === 'deal-pipeline') {
          dispatch(updateDealRecord({ dealId: recordIdString, updates: result.data }))
        } else if (module === 'fundraising' || module === 'fundraising-pipeline') {
          dispatch(updateProspectRecord({ prospectId: recordIdString, updates: result.data }))
        }
        toast.success('Refreshed Successfully')

        // Refresh interactions section if available
        if (refreshInteractionsRef.current) {
          await refreshInteractionsRef.current()
        }

        // Refresh tasks section if available
        if (refreshTasksRef.current) {
          await refreshTasksRef.current()
        }

        // Refresh investment measures section if available
        if (refreshInvestmentMeasuresRef.current) {
          await refreshInvestmentMeasuresRef.current()
        }
      }
    } catch (error) {
      console.error(`Error refreshing ${module}:`, error)
    } finally {
      setDrawerLoading(false)
    }
  }, [recordId, activeTab?.module, isNew, dispatch])

  // Handle drawer submit - triggers form submission via ref
  const handleDrawerSubmit = useCallback(async () => {
    if (drawerFormRef.current && drawerFormRef.current.submit) {
      await drawerFormRef.current.submit()
    }
  }, [])

  // Callback for when drawer form completes submission
  const handleDrawerFormSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        showErrorToast('Your session has expired — please log in again and try saving once more')
        return
      }

      // A Save that cannot complete must always tell the user why (owner rule
      // 2026-09-02). The services already turn backend envelopes into a
      // sentence in result.error; this is the single place it reaches a toast.
      // One keyed toast per drawer module: a repeat failure updates it rather
      // than stacking, and a later success (saveSucceeded) takes it down.
      const saveToastKey = `drawer-save:${activeTab?.module || 'record'}`
      const reportSaveFailure = (what: string, err: unknown) => {
        console.error(`Failed to ${what}:`, err)
        const text = typeof err === 'string' && err.trim() ? err : err instanceof Error ? err.message : undefined
        showErrorToast(text || `Failed to ${what}`, { key: saveToastKey })
      }
      const saveSucceeded = () => {
        dismissErrorToast(saveToastKey)
        closeDrawer()
      }

      setDrawerLoading(true)
      try {
        if (activeTab?.module === 'deals' || activeTab?.module === 'deal-pipeline') {
          if (isNew) {
            // Place the new deal at the top of its pipeline stage (shared
            // with the global quick-add drawer).
            assignNewDealSequence(formData, pipelineDeals, deals)

            const result = await createDealService(formData, currentToken, dispatch)
            if (result.data) {
              // Clean up temporary record on successful submission
              if (tempRecordUuid && activeTab?.module) {
                const moduleName = activeTab.module
                dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
                dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
                // Increment counter per module
                setNewDrawerOpenCount((prev) => ({ ...prev, [moduleName]: (prev[moduleName] || 0) + 1 }))
                // Mark when this module's drawer was closed
                drawerCloseTimeRef.current[moduleName] = Date.now()
              }
              saveSucceeded()
            } else {
              reportSaveFailure('create deal', result.error)
            }
          } else {
            saveSucceeded()
          }
        } else if (activeTab?.module === 'companies') {
          if (isNew) {
            const result = await createCompanyService(formData, currentToken, dispatch)
            if (result.data) {
              // Clean up temporary record on successful submission
              if (tempRecordUuid && activeTab?.module) {
                const moduleName = activeTab.module
                dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
                dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
                // Increment counter per module
                setNewDrawerOpenCount((prev) => ({ ...prev, [moduleName]: (prev[moduleName] || 0) + 1 }))
                // Mark when this module's drawer was closed
                drawerCloseTimeRef.current[moduleName] = Date.now()
              }
              saveSucceeded()
            } else {
              reportSaveFailure('create company', result.error)
            }
          } else {
            if (!recordId) {
              reportSaveFailure('update company', 'No record is open in the drawer')
              return
            }
            const result = await updateCompanyService(recordId, formData, currentToken, dispatch)
            if (result.data) {
              saveSucceeded()
            } else {
              reportSaveFailure('update company', result.error)
            }
          }
        } else if (activeTab?.module === 'contacts') {
          if (isNew) {
            const result = await createContactService(formData, currentToken, dispatch)
            if (result.data) {
              // Clean up temporary record on successful submission
              if (tempRecordUuid && activeTab?.module) {
                const moduleName = activeTab.module
                dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
                dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
                // Increment counter per module
                setNewDrawerOpenCount((prev) => ({ ...prev, [moduleName]: (prev[moduleName] || 0) + 1 }))
                // Mark when this module's drawer was closed
                drawerCloseTimeRef.current[moduleName] = Date.now()
              }
              saveSucceeded()
            } else {
              reportSaveFailure('create contact', result.error)
            }
          } else {
            if (!recordId) {
              reportSaveFailure('update contact', 'No record is open in the drawer')
              return
            }
            const result = await updateContactService(recordId, formData, currentToken, dispatch)
            if (result.data) {
              saveSucceeded()
            } else {
              reportSaveFailure('update contact', result.error)
            }
          }
        } else if (activeTab?.module === 'fundraising' || activeTab?.module === 'fundraising-pipeline') {
          if (isNew) {
            const result = await createFundraisingService(formData, currentToken, dispatch)
            if (result.data) {
              // Clean up temporary record on successful submission
              if (tempRecordUuid && activeTab?.module) {
                const moduleName = activeTab.module
                dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
                dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
                // Increment counter per module
                setNewDrawerOpenCount((prev) => ({ ...prev, [moduleName]: (prev[moduleName] || 0) + 1 }))
                // Mark when this module's drawer was closed
                drawerCloseTimeRef.current[moduleName] = Date.now()
              }
              saveSucceeded()
            } else {
              reportSaveFailure('create prospect', result.error)
            }
          } else {
            if (!recordId) {
              reportSaveFailure('update prospect', 'No record is open in the drawer')
              return
            }
            const result = await updateFundraisingService(recordId, formData, currentToken, dispatch)
            if (result.data) {
              saveSucceeded()
            } else {
              reportSaveFailure('update prospect', result.error)
            }
          }
        } else if (activeTab?.module === 'prospects') {
          if (isNew) {
            const result = await dispatch(createProspectThunk(formData))
            if (createProspectThunk.fulfilled.match(result)) {
              saveSucceeded()
            } else {
              reportSaveFailure('create prospect', result.payload)
            }
          } else {
            saveSucceeded()
          }
        }
        // Add handling for other modules as needed
      } catch (error) {
        reportSaveFailure('save record', error)
      } finally {
        setDrawerLoading(false)
      }
    },
    [activeTab?.module, isNew, recordId, closeDrawer, dispatch, pipelineDeals, deals, tempRecordUuid]
  )

  const handleDrawerCancel = useCallback(() => {
    // Close drawer first to hide it immediately
    closeDrawer()
    // Then clean up state after drawer is closed
    setTimeout(() => {
      // Clean up temporary record when drawer closes
      if (isNew && tempRecordUuid && activeTab?.module) {
        const moduleName = activeTab.module
        dispatch(unregisterRecordUsage({ uuid: tempRecordUuid, context: 'drawer' }))
        dispatch(removeActiveRecord({ uuid: tempRecordUuid }))
        // Increment counter per module
        setNewDrawerOpenCount((prev) => ({ ...prev, [moduleName]: (prev[moduleName] || 0) + 1 }))
        // Mark when this module's drawer was closed
        drawerCloseTimeRef.current[moduleName] = Date.now()
      }
      setSelectedRecord(null)
    }, 100)
  }, [closeDrawer, isNew, tempRecordUuid, activeTab?.module, dispatch])

  const handleDrawerDeleteRecord = useCallback(async () => {
    if (!recordId || isNew || !activeTab?.module) return
    const mod = activeTab.module
    if (
      mod !== 'contacts' &&
      mod !== 'companies' &&
      mod !== 'deals' &&
      mod !== 'deal-pipeline' &&
      mod !== 'fundraising' &&
      mod !== 'fundraising-pipeline' &&
      mod !== 'prospects'
    )
      return

    const token = getAccessToken(store.getState().auth)
    if (!token) {
      showErrorToast('Not authenticated')
      return
    }

    setDrawerLoading(true)
    try {
      const id = String(recordId)
      if (mod === 'contacts') {
        await deleteContact(id, token)
        dispatch(removeContactFromList({ contactId: id }))
      } else if (mod === 'companies') {
        await deleteCompany(id, token)
        dispatch(removeCompanyFromList({ companyId: id }))
      } else if (mod === 'fundraising' || mod === 'fundraising-pipeline' || mod === 'prospects') {
        await deleteProspect(id, token)
        dispatch(removeProspectFromList({ prospectId: id }))
      } else {
        await deleteDeal(id, token)
        dispatch(removeDealFromList({ dealId: id }))
      }
      toast.success('Record deleted')
      dispatch(unregisterRecordUsage({ uuid: id, context: 'drawer' }))
      dispatch(removeActiveRecord({ uuid: id }))
      closeDrawer()
      setTimeout(() => setSelectedRecord(null), 100)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete record'
      showErrorToast(message)
    } finally {
      setDrawerLoading(false)
    }
  }, [recordId, isNew, activeTab?.module, dispatch, closeDrawer])

  const drawerDeleteConfirm = useMemo(() => {
    if (!isDrawerOpen || isNew || !recordId || !activeTab?.module) return null
    const mod = activeTab.module
    if (mod === 'contacts') {
      return {
        title: CONFIRM_DELETE_CONTACT_TITLE,
        onConfirm: handleDrawerDeleteRecord
      }
    }
    if (mod === 'companies') {
      return {
        title: CONFIRM_DELETE_COMPANY_TITLE,
        onConfirm: handleDrawerDeleteRecord
      }
    }
    if (mod === 'deals' || mod === 'deal-pipeline') {
      return {
        title: CONFIRM_DELETE_DEAL_TITLE,
        onConfirm: handleDrawerDeleteRecord
      }
    }
    if (mod === 'fundraising' || mod === 'fundraising-pipeline' || mod === 'prospects') {
      return {
        title: CONFIRM_DELETE_PROSPECT_TITLE,
        onConfirm: handleDrawerDeleteRecord
      }
    }
    return null
  }, [isDrawerOpen, isNew, recordId, activeTab?.module, handleDrawerDeleteRecord])

  // Record provenance for the drawer banner — the system-wide slot RightMenu
  // renders (owner 2026-09-16). Per-module because the field keys are
  // entity-scoped; a module whose detail RPC does not project
  // `<table>__created_by_name` / `__date_created` yet simply returns null and
  // the banner shows nothing. Same shape and lifecycle as drawerDeleteConfirm
  // directly above.
  const drawerProvenance = useMemo(() => {
    if (!isDrawerOpen || isNew || !recordId || !activeTab?.module) return null
    if (!isDealsModule) return null
    // Read the DETAIL record, not selectedRecord. selectedRecord can be the
    // grid row, and created_by_name is deliberately NULL in grid mode (the
    // RPC gates the per-row resolver to MODE_DEAL_IDS), which would show a
    // bare date with no creator. activeRecords holds the by-id read the
    // drawer itself fetches.
    const record = dealFromActiveRecords as Record<string, unknown> | null
    if (!record) return null
    return {
      createdByName: (record[DealFields.deals__created_by_name] as string | null) ?? null,
      dateCreated: (record[DealFields.deals__date_created] as string | null) ?? null,
      agentProvenance: (record[DealFields.prog__created_provenance] as AgentProvenanceEntry | null) ?? null
    }
  }, [isDrawerOpen, isNew, recordId, activeTab?.module, isDealsModule, dealFromActiveRecords])

  // Determine if drawer actions should be shown
  const shouldShowDrawerActions = () => {
    if (!isDrawerOpen || !activeTab) return true

    // Hide actions for existing deal drawers only
    if ((activeTab.module === 'deals' || activeTab.module === 'deal-pipeline') && !isNew) {
      return false
    }

    // Hide actions for existing company drawers
    if (activeTab.module === 'companies' && !isNew) {
      return false
    }

    // Hide actions for existing contact drawers
    if (activeTab.module === 'contacts' && !isNew) {
      return false
    }

    // Hide actions for existing fundraising drawers
    if ((activeTab.module === 'fundraising' || activeTab.module === 'fundraising-pipeline') && !isNew) {
      return false
    }

    // Show actions for all other cases (new deals, new companies, new contacts, all other modules)
    return true
  }

  // Create stable key for drawer to prevent remounting when switching tabs
  // For new drawers, include open count per module to force remount when drawer reopens after close
  const activeTabModuleForDrawerKey = activeTab?.module
  const drawerKey = useMemo(() => {
    if (!isDrawerOpen || !activeTabModuleForDrawerKey || !recordId) return null
    if (isNew) {
      const moduleCount = newDrawerOpenCount[activeTabModuleForDrawerKey] || 0
      return `${activeTabModuleForDrawerKey}-new-${moduleCount}`
    }
    return `${activeTabModuleForDrawerKey}-${recordId}`
  }, [isDrawerOpen, activeTabModuleForDrawerKey, isNew, recordId, newDrawerOpenCount])

  // Determine drawer content based on active tab - memoized to prevent remounting on tab navigation
  // Optimized to only recalculate when drawerKey changes (which encodes module + recordId + isNew)
  const activeTabModule = activeTab?.module
  const drawerContent = useMemo(() => {
    if (!isDrawerOpen || !activeTabModule) return null

    switch (activeTabModule) {
      case 'deals':
      case 'deal-pipeline':
        if (isNew) {
          return (
            <DealDrawerNew
              key={drawerKey}
              ref={drawerFormRef}
              deal={selectedRecord}
              module={activeTabModule}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
            />
          )
        } else {
          return (
            <DealDrawerExisting
              key={drawerKey}
              deal={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
              onRefreshInteractionsReady={(refreshFn) => {
                refreshInteractionsRef.current = refreshFn
              }}
              onRefreshTasksReady={(refreshFn) => {
                refreshTasksRef.current = refreshFn
              }}
            />
          )
        }
      case 'companies':
        if (isNew) {
          return (
            <CompanyDrawerNew
              key={drawerKey}
              ref={drawerFormRef}
              company={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
            />
          )
        } else {
          return (
            <CompanyDrawerExisting
              key={drawerKey}
              company={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
              onRefreshInteractionsReady={(refreshFn) => {
                refreshInteractionsRef.current = refreshFn
              }}
              onRefreshTasksReady={(refreshFn) => {
                refreshTasksRef.current = refreshFn
              }}
              onRefreshInvestmentMeasuresReady={(refreshFn) => {
                refreshInvestmentMeasuresRef.current = refreshFn
              }}
            />
          )
        }
      case 'contacts':
        if (isNew) {
          return (
            <ContactDrawerNew
              key={drawerKey}
              ref={drawerFormRef}
              contact={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
            />
          )
        } else {
          return (
            <ContactDrawerExisting
              key={drawerKey}
              contact={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
            />
          )
        }
      case 'fundraising':
      case 'fundraising-pipeline':
        if (isNew) {
          return (
            <FundraisingDrawerNew
              key={drawerKey}
              ref={drawerFormRef}
              prospect={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
            />
          )
        } else {
          return (
            <FundraisingDrawerExisting
              key={drawerKey}
              prospect={selectedRecord}
              onSubmit={handleDrawerFormSubmit}
              loading={drawerLoading}
              onRefreshInteractionsReady={(refreshFn) => {
                refreshInteractionsRef.current = refreshFn
              }}
              onRefreshTasksReady={(refreshFn) => {
                refreshTasksRef.current = refreshFn
              }}
            />
          )
        }
      // Rounds have a full form only (no drawer). Add other module drawers as needed
      default:
        return null
    }
  }, [isDrawerOpen, activeTabModule, drawerKey, selectedRecord, drawerLoading, handleDrawerFormSubmit, isNew])

  // Determine drawer title based on active tab
  const getDrawerTitle = () => {
    if (!activeTab) return ''

    if (activeTab.module === 'deals' || activeTab.module === 'deal-pipeline') {
      if (isNew) {
        return 'New Deal'
      }
      return recordText(selectedRecord, DealFields.deals__name) ?? `Edit Deal ${recordId ? `(ID: ${recordId})` : ''}`
    }

    if (activeTab.module === 'companies') {
      if (isNew) {
        return 'New Company'
      }
      return (
        recordText(selectedRecord, AccountRecordFields.accounts__name) ??
        `Edit Company ${recordId ? `(ID: ${recordId})` : ''}`
      )
    }

    if (activeTab.module === 'contacts') {
      if (isNew) {
        return 'New Contact'
      }
      const lastName = recordText(selectedRecord, ContactRecordFields.contacts__last_name)
      if (lastName) {
        const firstName = recordText(selectedRecord, ContactRecordFields.contacts__first_name) || ''
        return `${firstName} ${lastName}`.trim() || lastName
      }
      return `Edit Contact ${recordId ? `(ID: ${recordId})` : ''}`
    }

    if (activeTab.module === 'fundraising' || activeTab.module === 'fundraising-pipeline') {
      if (isNew) {
        return 'New Prospect'
      }
      return (
        recordText(selectedRecord, AccountRecordFields.accounts__name) ??
        recordText(selectedRecord, 'investor_name') ??
        `Edit Prospect ${recordId ? `(ID: ${recordId})` : ''}`
      )
    }

    if (activeTab.module === 'prospects') {
      if (isNew) {
        return 'New Prospect'
      }
      return recordText(selectedRecord, 'investor_name') ?? `Edit Prospect ${recordId ? `(ID: ${recordId})` : ''}`
    }

    if (activeTab.module === 'asset-transactions') {
      if (isNew) {
        return 'New Asset Transaction'
      }
      return recordText(selectedRecord, 'name') ?? `Edit Asset Transaction ${recordId ? `(ID: ${recordId})` : ''}`
    }

    if (activeTab.module === 'contacts') {
      if (isNew) {
        return 'New Contact'
      }
      const lastName = recordText(selectedRecord, ContactRecordFields.contacts__last_name)
      if (lastName) {
        const firstName = recordText(selectedRecord, ContactRecordFields.contacts__first_name) || ''
        return `${firstName} ${lastName}`.trim() || lastName
      }
      return `Edit Contact ${recordId ? `(ID: ${recordId})` : ''}`
    }

    // Default behavior for other modules
    const moduleName = activeTab.module.charAt(0).toUpperCase() + activeTab.module.slice(1)
    return isNew ? `New ${moduleName}` : `Edit ${moduleName} ${recordId ? `(ID: ${recordId})` : ''}`
  }

  // Below the mobile breakpoint, render the read-only mobile shell instead of the
  // desktop layout. All shared infra (Redux, Router, AuthGuard, tab/drawer providers)
  // lives above this component in App.tsx, so the mobile shell inherits it. This branch
  // sits after all hooks above, so the Rules of Hooks are preserved.
  if (isMobile) {
    return <MobileShell />
  }

  return (
    <Wrapper
      menuSections={menuConfig.sections}
      footerSelectItems={menuConfig.footerSelectItems}
      currentPath={location.pathname}
      footerSelectionKey={displayAreaKey}
      selectedMenuKeys={selectedMenuKeys}
      showTabNavigation={true}
      showRightMenu={true}
      drawerContent={drawerContent}
      drawerTitle={getDrawerTitle()}
      onDrawerCancel={handleDrawerCancel}
      onDrawerSubmit={handleDrawerSubmit}
      onDrawerRefresh={handleDrawerRefresh}
      drawerLoading={drawerLoading}
      viewFullRecord={true}
      rightMenuWidth={rightMenuWidth}
      showDrawerActions={shouldShowDrawerActions()}
      selectedRecord={selectedRecord}
      drawerDeleteConfirm={drawerDeleteConfirm}
      provenance={drawerProvenance}
      // No children - content is managed by TabNavigation component
    />
  )
}

/**
 * Main Tab Container - Wraps all authenticated routes with tab functionality
 * Provides drawer functionality for forms and manages tab state
 */
function MainTabContainer() {
  return (
    <RightMenuProvider>
      <MainTabContainerInner />
    </RightMenuProvider>
  )
}

export default MainTabContainer
